import { Composite } from '@floating-ui/react';
import classNames from 'classnames';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useImperativeHandle,
  useRef,
  type HTMLAttributes,
  type JSX,
  type RefObject,
} from 'react';
import type { StrateListElement, StrateProps } from './Strate.types';
import type { StrateItemProps } from './StrateItem/StrateItem.types';

/**
 * `Strate` is a component used to display a list of elements horizontally.
 * It supports `navigation` slot to render navigation controls for the list.
 * It support rendering the list as floating-ui's `Composite` component, which allows arrow navigation.
 * It supports customizing the list style through `listClassName`.
 * It forwards the `ref` and the rest HTML attributes (except for className) to the list element.
 *
 * @example
 *
 * ```tsx
 * <Strate
 *    containerAs="nav"
 *    navigation={<StrateNavigation />}
 *    listClassName="grid-config-classes"
 *    defaultScrollToIndex={2} // Automatically scrolls to the third item on mount
 * >
 *    <StrateItem>
 *      <Media aspectRatio="169" image={<MediaImage src={IMAGE.src169} sources={IMAGE.sources169} />} />
 *    </StrateItem>
 *    <StrateItem>
 *      <Media aspectRatio="169" image={<MediaImage src={IMAGE.src169} sources={IMAGE.sources169} />} />
 *    </StrateItem>
 * </Strate>
 * ```
 */
export const Strate = forwardRef<StrateListElement, StrateProps>(
  function Strate(
    {
      className,
      listAs: StrateList = 'ul',
      defaultScrollToIndex,
      scrollBehavior = 'smooth',
      composite = false,
      compositeProps,
      navigation,
      listClassName,
      as: Container = 'div',
      containerProps,
      children,
      ...listProps
    }: StrateProps,
    forwardedListRef,
  ): JSX.Element {
    const listRef = useRef<StrateListElement>(null);

    useImperativeHandle(forwardedListRef, () => listRef.current!, []);

    const items = Children.toArray(children).filter(
      isValidElement<StrateItemProps>,
    );

    useEffect(() => {
      if (!defaultScrollToIndex || !items.length) {
        return;
      }

      const scrollToItem = () => {
        if (!listRef.current?.children) {
          return;
        }
        const stratePaddingLeft = parseInt(
          window?.getComputedStyle(listRef.current).paddingLeft || '0',
          10,
        );
        const itemToScrollTo = listRef.current.children[
          defaultScrollToIndex
        ] as HTMLElement;
        if (!itemToScrollTo) {
          return;
        }
        listRef.current.scrollTo({
          left: itemToScrollTo.offsetLeft - stratePaddingLeft,
          behavior: scrollBehavior,
        });
      };

      // Ensures the browser has painted the DOM
      requestAnimationFrame(() => {
        // Ensures all rendering calculations are complete in some edge cases
        setTimeout(scrollToItem, 0);
      });
    }, [defaultScrollToIndex, items.length, scrollBehavior]);

    const strateListProps = {
      ...listProps,
      ['aria-orientation']: 'horizontal',
      className: classNames(
        // reset default styles
        'list-none m-dt-spacing-none p-dt-spacing-none',
        'overflow-x-auto no-scrollbar scroll-smooth whitespace-nowrap snap-x',
        listClassName,
      ),
    } satisfies HTMLAttributes<StrateListElement>;

    const list = composite ? (
      <Composite
        render={
          <StrateList>
            {items.map((item) =>
              cloneElement(item, {
                composite: true,
              }),
            )}
          </StrateList>
        }
        orientation="horizontal"
        {...strateListProps}
        {...compositeProps}
        ref={listRef}
      />
    ) : (
      <StrateList {...strateListProps} ref={listRef as RefObject<any>}>
        {items}
      </StrateList>
    );

    return (
      <Container
        {...containerProps}
        className={classNames('relative', className)}
      >
        {navigation
          ? cloneElement(navigation, {
              listRef,
              children: list,
            })
          : list}
      </Container>
    );
  },
);
